<template>
	<main class="auth_theme_container register_onboarding">
		<section class="form_panel">
			<header class="form_panel-header">
				<Logo class="brand_logo" />
			</header>
			<div class="form_panel-body">
				<div v-if="currentProgress !== null" class="wizard_progress">
					<div class="wizard_progress-track">
						<div
							class="wizard_progress-fill"
							:style="{ width: currentProgress + '%' }"
						></div>
					</div>
					<span class="wizard_progress-label">{{ currentProgress }}%</span>
				</div>
				<RegisterPersonalDataStep
					v-if="currentStep === 0"
					:value="registration"
					@continue="handleStepContinue"
				/>
				<RegisterCompanyDataStep
					v-else-if="currentStep === 1"
					:value="registration"
					:loading="isSubmitting"
					@continue="handleStepContinue"
				/>
				<RegisterSiiConnectStep
					v-else-if="currentStep === 2"
					@continue="handleStepContinue"
				/>
				<RegisterPersonalizationStep
					v-else-if="currentStep === 3"
					:value="registration"
					@continue="handleStepContinue"
				/>
				<p class="login_hint">
					¿Ya tienes una cuenta?
					<router-link class="login_hint-link" :to="{ name: 'Login' }">
						Iniciar sesión
					</router-link>
				</p>
			</div>
			<footer class="form_panel-footer">
				<span>© CFOremoto</span>
				<a class="support_link" href="mailto:soporte@cforemoto.com">
					<b-icon pack="fas" icon="envelope" size="is-small" />
					soporte@cforemoto.com
				</a>
			</footer>
		</section>
		<aside class="preview_panel" aria-hidden="true">
			<div class="preview_frame">
				<img :src="previewImage" alt="" />
			</div>
		</aside>
	</main>
</template>

<script>
import { register } from "@/api/security";
import previewImage from "@/assets/images/register_preview.png";
import RegisterCompanyDataStep from "@/components/register/RegisterCompanyDataStep.vue";
import RegisterPersonalDataStep from "@/components/register/RegisterPersonalDataStep.vue";
import RegisterPersonalizationStep from "@/components/register/RegisterPersonalizationStep.vue";
import RegisterSiiConnectStep from "@/components/register/RegisterSiiConnectStep.vue";
import Logo from "@/components/svg/Logo.vue";
import { setSession } from "@/utils/auth";
import { BASIC } from "@/utils/clientTypes";
import { consoleHandlerError } from "@/utils/message";
import SegmentAnalytics from "@/utils/segmentAnalytics";
import { mapMutations } from "vuex";

// Progreso del onboarding mostrado arriba del título de cada paso (null = sin
// barra, como pide el diseño para el paso 1). Los % los define el mockup.
const STEP_PROGRESS = [null, 10, 20, 80];
const COMPANY_STEP = 1;
const LAST_STEP = 3;

export default {
	name: "RegisterOnboarding",
	components: {
		Logo,
		RegisterCompanyDataStep,
		RegisterPersonalDataStep,
		RegisterPersonalizationStep,
		RegisterSiiConnectStep,
	},
	data() {
		return {
			currentStep: 0,
			isSubmitting: false,
			registration: {
				first_name: "",
				last_name: "",
				email: "",
				password: "",
				password_confirmation: "",
				role: "",
				company_name: "",
				company_rut: "",
				business_type: "",
				fantasy_name: "",
			},
			previewImage,
		};
	},
	computed: {
		currentProgress() {
			return STEP_PROGRESS[this.currentStep] ?? null;
		},
	},
	methods: {
		...mapMutations("userAuth", ["SET_LOGIN"]),
		async handleStepContinue(payload) {
			this.registration = { ...this.registration, ...payload };
			if (this.currentStep === COMPANY_STEP) {
				const registered = await this.signupUser();
				if (!registered) return;
			}
			if (this.currentStep < LAST_STEP) {
				this.currentStep += 1;
			} else {
				// Último paso (Personalización) completo → al dashboard.
				this.$router.push({ name: "dashboard" });
			}
		},
		async signupUser() {
			this.isSubmitting = true;
			const { registration } = this;
			try {
				const { data: user } = await register({
					first_name: registration.first_name,
					last_name: registration.last_name,
					email: registration.email,
					username: registration.email,
					password: registration.password,
					// Se persiste en la membresía UserCompany (rol por empresa).
					role: registration.role,
					company_name: registration.company_name,
					company_rut: registration.company_rut.split(".").join(""),
					client_type: BASIC,
					// Tipo de negocio: el backend resuelve el BusinessType y
					// genera el plan de cuentas (Productos+planta→Productor,
					// Productos→Comercializador, Servicios→Consultor).
					business_type: registration.business_type,
					has_production_plant: registration.has_production_plant === true,
					// Marca el alta self-service del onboarding: el backend
					// habilita por defecto Plan de Cuentas + Clasificador
					// (has_classificator) y el Reporte Financiero beta
					// (has_financial_report).
					onboarding: true,
				});
				setSession(user);
				this.SET_LOGIN(user);
				// Analytics nunca debe frenar el funnel: sin Segment cargado
				// (dev, adblock) el track lanza y el usuario ya está registrado.
				try {
					new SegmentAnalytics(user).trackRegisterFast();
				} catch (analyticsError) {
					// noop
				}
				return true;
			} catch (error) {
				// consoleHandlerError notifica y re-lanza; acá el error ya
				// está manejado (el wizard se queda en el paso de empresa).
				try {
					consoleHandlerError({ error });
				} catch (handled) {
					// noop
				}
				return false;
			} finally {
				this.isSubmitting = false;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.register_onboarding {
	display: flex;
	min-height: 100vh;
	background: var(--auth-surface);
}

.form_panel {
	flex: 1 1 50%;
	display: flex;
	flex-direction: column;
	padding: 32px 48px;
}

.form_panel-header {
	flex: 0 0 auto;
}

.brand_logo {
	width: 148px;
	height: auto;
}

.form_panel-body {
	flex: 1 1 auto;
	display: flex;
	flex-direction: column;
	justify-content: center;
	width: 100%;
	max-width: 384px;
	margin: 0 auto;
	padding: 48px 0;
}

.wizard_progress {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 16px;
}

.wizard_progress-track {
	flex: 1 1 auto;
	height: 6px;
	background: var(--auth-disabled-bg);
	border-radius: 3px;
	overflow: hidden;
}

.wizard_progress-fill {
	height: 100%;
	background: var(--auth-accent);
	border-radius: 3px;
	transition: width 0.3s ease-out;
}

.wizard_progress-label {
	flex: 0 0 auto;
	color: var(--auth-text);
	font-size: 13px;
}

.login_hint {
	margin-top: 24px;
	color: var(--auth-text-secondary);
	font-size: 14px;
	text-align: center;
}

.login_hint-link {
	color: var(--auth-accent);
	font-weight: 600;

	&:hover {
		color: var(--auth-accent-hover);
	}
}

.form_panel-footer {
	flex: 0 0 auto;
	display: flex;
	justify-content: space-between;
	align-items: center;
	color: var(--auth-text-muted);
	font-size: 13px;
}

.support_link {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	color: var(--auth-text-muted);

	&:hover {
		color: var(--auth-text-secondary);
	}
}

.preview_panel {
	flex: 1 1 50%;
	// min-width:0 evita el min-width:auto de flex que, al no encoger el panel
	// por debajo del ancho de la imagen, generaba scroll horizontal.
	min-width: 0;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	overflow: hidden;
	// Aire arriba/abajo: el frame no toca los bordes del panel.
	padding: 40px 0;
	background: var(--auth-panel-bg);
	border-left: 1px solid var(--auth-border-soft);
}

// El frame se dimensiona contra el viewport (no contra el panel estirado) para
// no deformar la captura ni desbordar: la imagen escala desde su resolución
// nativa hacia abajo (siempre nítida) y sangra por la derecha, recortada por el
// overflow del panel. El screenshot es la promesa del producto, no una card.
.preview_frame {
	flex: 0 0 auto;
	margin-left: clamp(32px, 12%, 96px);
	padding: 12px;
	background: var(--auth-frame);
	border-radius: 28px;
	box-shadow: 0 24px 60px rgba(22, 36, 47, 0.35);

	img {
		display: block;
		height: calc(100vh - 104px);
		width: auto;
		border-radius: 18px;
	}
}

@media (max-width: 1023px) {
	.preview_panel {
		display: none;
	}

	.form_panel {
		padding: 24px;
	}
}
</style>
